import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Copy, RefreshCw, PackageX } from "lucide-react";
import { api, apiError } from "@/lib/api";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return ""; }
};

export function RestockWaitlist() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/restock-waitlist").then((r) => setRows(r.data || [])).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const copyEmails = (emails) => {
    const text = emails.join(", ");
    const fallback = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success("Emails copied");
      } catch { toast.error("Couldn't copy — select the emails manually"); }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => toast.success("Emails copied")).catch(fallback);
    } else { fallback(); }
  };

  const totalWaiting = rows.reduce((s, r) => s + r.count, 0);

  return (
    <div className="mt-6" data-testid="restock-waitlist">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold">Restock Demand</h2>
          <p className="text-xs text-muted-foreground">
            {loading ? "Loading…" : `${totalWaiting} customer${totalWaiting === 1 ? "" : "s"} waiting across ${rows.length} product${rows.length === 1 ? "" : "s"} · most-wanted first`}
          </p>
        </div>
        <button data-testid="restock-waitlist-refresh" onClick={load} className="inline-flex items-center gap-1.5 border border-border px-3 py-2 mono text-[11px] uppercase tracking-[0.15em] hover:border-primary hover:text-primary">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {!loading && rows.length === 0 && (
        <div className="mt-6 border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="restock-waitlist-empty">
          <PackageX className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
          No one is waiting on a restock right now. When a product sells out, customers who tap “Notify me” will show up here.
        </div>
      )}

      <div className="mt-4 space-y-4">
        {rows.map((r) => (
          <div key={r.flavor_id} className="border border-border bg-card p-4" data-testid={`restock-waitlist-${r.flavor_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                {r.image_url ? (
                  <span className="h-12 w-9 shrink-0 overflow-hidden border border-border bg-white"><img src={r.image_url} alt="" className="h-full w-full object-cover" /></span>
                ) : <span className="h-12 w-9 shrink-0 bg-muted" />}
                <div>
                  <div className="font-medium">{r.name}{r.brand_name ? <span className="text-muted-foreground"> · {r.brand_name}</span> : null}</div>
                  <div className="mono mt-0.5 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                    {r.range_type === "guest" ? "Guest" : "DreamLabs"} · {r.stock <= 0 ? <span className="text-destructive">out of stock</span> : `${r.stock} in stock`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="mono inline-flex items-center gap-1.5 border border-primary bg-primary/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-primary" data-testid={`restock-waitlist-count-${r.flavor_id}`}>
                  <BellRing className="h-3.5 w-3.5" /> {r.count} waiting
                </span>
                <button data-testid={`restock-waitlist-copy-${r.flavor_id}`} onClick={() => copyEmails(r.subscribers.map((s) => s.email))}
                  className="inline-flex items-center gap-1.5 border border-border px-2.5 py-1 mono text-[11px] uppercase tracking-[0.12em] hover:border-primary hover:text-primary">
                  <Copy className="h-3.5 w-3.5" /> Copy emails
                </button>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-1.5 border-t border-border pt-3 sm:grid-cols-2 lg:grid-cols-3">
              {r.subscribers.map((s, i) => (
                <div key={`${s.email}-${i}`} className="flex items-center justify-between gap-2 border border-border/60 px-2 py-1 text-sm">
                  <span className="truncate">{s.email}</span>
                  <span className="mono shrink-0 text-[10px] text-muted-foreground">{fmtDate(s.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
